// src/frontend/src/pages/TermsOfService.tsx
import React from 'react';
import HomeLayout from '../components/Layout/HomeLayout';

const TermsOfService: React.FC = () => {
  return (
    <HomeLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing or using PM Coach ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
                If you disagree with any part of these terms, you may not access the Service.
              </p>
              <p className="text-gray-700 mb-4">
                These Terms apply to all visitors, users, and others who access or use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                PM Coach is an AI-powered training platform that helps project managers improve their stakeholder 
                management skills through realistic conversational simulations, feedback, and coaching.
              </p>
              <p className="text-gray-700 mb-4">
                The Service includes:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>AI-powered stakeholder conversation simulations</li>
                <li>Real-time coaching tips and feedback</li>
                <li>Performance analytics and progress tracking</li>
                <li>Custom scenario creation tools</li>
                <li>Team management features (for applicable plans)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Registration</h3>
              <p className="text-gray-700 mb-4">
                You must create an account to use PM Coach. You agree to provide accurate, current, and complete 
                information during registration and to update such information to keep it accurate.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Account Security</h3>
              <p className="text-gray-700 mb-4">
                You are responsible for:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Account Termination</h3>
              <p className="text-gray-700 mb-4">
                You may terminate your account at any time. We may terminate accounts that violate these Terms 
                or for other legitimate business reasons with reasonable notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Subscription Plans and Payment</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Subscription Terms</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li><strong>Free Trial:</strong> 14 days with full access to Solo Core features</li>
                <li><strong>Solo Core:</strong> $19/month for individual users</li>
                <li><strong>Solo Pro:</strong> $39/month with advanced features</li>
                <li><strong>Team Pilot:</strong> $950 one-time for 10 seats, 60 days</li>
                <li><strong>Enterprise:</strong> Custom pricing for larger organizations</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Billing and Cancellation</h3>
              <p className="text-gray-700 mb-4">
                Subscriptions automatically renew unless cancelled. You may cancel at any time through your 
                account settings. Cancellations take effect at the end of the current billing period.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Refunds</h3>
              <p className="text-gray-700 mb-4">
                We offer prorated refunds for annual subscriptions cancelled within 30 days. Monthly subscriptions 
                are generally not refundable, but we will consider requests on a case-by-case basis.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Price Changes</h3>
              <p className="text-gray-700 mb-4">
                We may change subscription prices with 30 days' notice. Existing subscribers will be grandfathered 
                at their current rate for the remainder of their billing cycle.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Usage Guidelines</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Acceptable Use</h3>
              <p className="text-gray-700 mb-4">You may use PM Coach for legitimate professional development purposes. You agree not to:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to reverse engineer or copy our AI models</li>
                <li>Share account credentials with others</li>
                <li>Create multiple accounts to circumvent usage limits</li>
                <li>Use automated systems to access the Service</li>
                <li>Interfere with the Service's security features</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Content Guidelines</h3>
              <p className="text-gray-700 mb-4">
                When creating custom scenarios or interacting with the Service, you agree not to include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Illegal, harmful, or offensive content</li>
                <li>Personal information about real individuals</li>
                <li>Confidential or proprietary information</li>
                <li>Content that violates intellectual property rights</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Our Rights</h3>
              <p className="text-gray-700 mb-4">
                PM Coach and its original content, features, and functionality are owned by us and are protected 
                by international copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Your Content</h3>
              <p className="text-gray-700 mb-4">
                You retain ownership of content you create (custom scenarios, responses). By using the Service, 
                you grant us a limited license to use your content to provide and improve the Service.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">License to Use</h3>
              <p className="text-gray-700 mb-4">
                We grant you a limited, non-exclusive, non-transferable license to use PM Coach in accordance 
                with these Terms and your subscription plan.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Privacy and Data</h2>
              <p className="text-gray-700 mb-4">
                Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect 
                your information. By using the Service, you consent to our privacy practices.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>AI Processing:</strong> Your simulation conversations are processed by AI systems to provide 
                coaching feedback. This data is used solely for your benefit and is not shared with third parties 
                except as described in our Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Disclaimers and Limitations</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Service Availability</h3>
              <p className="text-gray-700 mb-4">
                We strive for high availability but cannot guarantee uninterrupted service. We may perform 
                maintenance that temporarily affects access.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Educational Purpose</h3>
              <p className="text-gray-700 mb-4">
                PM Coach is for educational and training purposes. While our simulations are realistic, they 
                should not replace real-world experience or professional judgment.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">No Professional Advice</h3>
              <p className="text-gray-700 mb-4">
                PM Coach provides training and feedback but does not constitute professional consulting or advice. 
                Always use your professional judgment in real-world situations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, PM Coach shall not be liable for any indirect, incidental, 
                special, consequential, or punitive damages, including without limitation, loss of profits, data, 
                use, goodwill, or other intangible losses.
              </p>
              <p className="text-gray-700 mb-4">
                Our total liability shall not exceed the amount you paid for the Service in the 12 months 
                preceding the claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Indemnification</h2>
              <p className="text-gray-700 mb-4">
                You agree to indemnify and hold PM Coach harmless from any claims arising from your use of 
                the Service, violation of these Terms, or infringement of any rights of another party.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Governing Law and Disputes</h2>
              <p className="text-gray-700 mb-4">
                These Terms are governed by the laws of New Jersey, United States. Any disputes will be 
                resolved through binding arbitration in accordance with the American Arbitration Association rules.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We may modify these Terms at any time. We will notify users of material changes via email or 
                through the Service. Continued use after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about these Terms, contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> legal@pmcoach.app<br />
                  <strong>Address:</strong> PM Coach, Princeton, NJ<br />
                  <strong>Phone:</strong> +1 (848) 248-5351
                </p>
              </div>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500">
                By using PM Coach, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </HomeLayout>
  );
};

export default TermsOfService;